import axios from 'axios';
import { useEffect, useState } from 'react';

import type { AnalyticsDateRange } from '@/types/analytics';
import type { WidgetDraft, WidgetResult } from '@/types/dashboards';

/**
 * A widget is previewable once it can produce a query:
 * - comparison: an axis + ≥1 series, each with source/measure/aggregation (and a
 *   date field when the axis is time);
 * - single: a source + measure + aggregation, plus a dimension unless it's a KPI.
 */
export function isWidgetComplete(widget: WidgetDraft): boolean {
    if (widget.mode === 'comparison') {
        if (!widget.axis || !widget.series || widget.series.length === 0) {
            return false;
        }

        return widget.series.every(
            (serie) => serie.source && serie.measure && serie.aggregation && (widget.axis !== 'time' || Boolean(serie.date_field)),
        );
    }

    if (!widget.source || !widget.measure || !widget.aggregation) {
        return false;
    }

    return widget.chart_type === 'kpi' || Boolean(widget.dimension);
}

/**
 * Debounced live preview for the builder: POSTs the in-progress widget to
 * dashboards.preview whenever it (or the date range) changes and returns the
 * normalized result. In-flight requests are aborted on change so a slow early
 * response can't overwrite a newer one.
 */
export function useWidgetPreview(widget: WidgetDraft, dateRange: AnalyticsDateRange) {
    const [result, setResult] = useState<WidgetResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const complete = isWidgetComplete(widget);
    const requestKey = JSON.stringify({ widget, dateRange });

    useEffect(() => {
        if (!complete) {
            setResult(null);
            setError(null);
            return;
        }

        const controller = new AbortController();
        const timer = setTimeout(() => {
            setLoading(true);
            setError(null);

            axios
                .post(
                    route('dashboards.preview'),
                    { ...widget, from_date: dateRange.from_date, to_date: dateRange.to_date },
                    { signal: controller.signal },
                )
                .then((response) => setResult(response.data as WidgetResult))
                .catch((err) => {
                    if (!axios.isCancel(err)) {
                        setError('preview_failed');
                    }
                })
                .finally(() => setLoading(false));
        }, 400);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
        // requestKey captures widget + dateRange; complete gates the fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey, complete]);

    return { result, loading, error };
}
